import { AsyncPipe, NgIf, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Dossier } from '@sc/data-access-models';
import { InsuranceCoverSandboxService } from '@sc/data-access-state-management';
import { TranslatorService } from '@sc/data-access-translation';
import { PhoneNumberPipe } from '@sc/feature-contextual-display';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { ButtonComponent, SinisterCommentComponent } from '@sc/ui-components';

@Component({
  selector: 'sc-declaration-end-information',
  styleUrls: ['./declaration-end-information.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    MatTooltipModule,
    ButtonComponent,
    SinisterCommentComponent,
    AsyncPipe,
    TitleCasePipe,
    TranslateModule,
    PhoneNumberPipe,
  ],
  template: `
    <section>
      <h2 class="sc-design-title-h1">
        {{ 'DeclarationEndInformation.informationTitle' | translate }}
      </h2>
      <aside class="_name">
        <div>
          <p>
            <span
              *ngIf="
                dossier?.customer?.firstname || dossier?.customer?.lastname
              "
            >
              {{ dossier.customer.firstname | titlecase }}
              {{ dossier.customer.lastname | titlecase }}
            </span>
          </p>
          <p
            *ngIf="dossier?.customer?.email"
            class="mailTitle"
            [matTooltip]="dossier?.customer?.email"
            matTooltipClass="matTooltipClassCustom"
          >
            {{ dossier?.customer?.email }}
          </p>
          <p *ngIf="dossier?.customer?.phoneNumber">
            {{ dossier?.customer?.phoneNumber | phoneNumber | async }}
          </p>
          <sc-button
            class="sc-design-button"
            *ngIf="
              featureFlippingService.isFeatureActivated(
                'b2b-can-resend-customer-notification'
              ) &&
              !isDossierAbandonned() &&
              !!dossier?.customer?.email &&
              !isRequestAppointment()
            "
            buttonType="mainWhite"
            leftIcon="mail"
            (click)="notifyCustomer()"
            >{{
              'DeclarationEndInformation.notifyCustomerButton' | translate
            }}</sc-button
          >
        </div>
      </aside>

      <aside
        class="additional-infos"
        *ngIf="
          featureFlippingService.isFeatureActivated(
            'b2c-field-display-marketing-data'
          ) ||
          featureFlippingService.isFeatureActivated(
            'b2b-field-display-marketing-data'
          )
        "
      >
        <div>
          <p
            [innerHTML]="
              'DeclarationEndInformation.acceptedMarketingData'
                | translate: { accepted: +!!dossier?.marketingData?.accepted }
            "
          ></p>
        </div>
      </aside>

      <aside class="_insurer">
        <p>
          {{ 'DeclarationEndInformation.assuranceTitle' | translate }}
          <span>
            {{ getFormattedInsuranceName() }}
          </span>
        </p>
        <p *ngIf="dossier?.policyInformation?.policyNumber">
          {{ 'DeclarationEndInformation.societyNumTitle' | translate }}
          <span>{{ dossier?.policyInformation?.policyNumber }}</span>
        </p>
        <div
          *ngIf="!!(insuranceMessage$ | async)"
          class="insurance-infos-container"
        >
          <p
            class="insurance-text sc-design-text-3"
            [innerHTML]="insuranceMessage$ | async"
          ></p>
          <p
            *ngIf="!!(coverMessage$ | async)"
            class="cover-text sc-design-text-3"
            [innerHTML]="coverMessage$ | async"
          ></p>
          <p
            *ngIf="!!(coverRangeMessage$ | async)"
            class="cover-range-text sc-design-text-3"
            [innerHTML]="coverRangeMessage$ | async"
          ></p>
        </div>
        <p
          *ngIf="
            featureFlippingService.isFeatureActivated(
              'b2c-can-declare-car-ownership'
            ) &&
            featureFlippingService.isBrandFeatureActivated(
              dossier.appointment?.brandId ||
                dossier.requestAppointment?.brandId,
              'fitter-allow-car-ownership'
            ) &&
            !!dossier?.claim?.car?.ownerShip?.type
          "
        >
          {{ 'DeclarationEndInformation.carOwnerShipLabel' | translate }}
          <span>{{
            dossier?.claim?.car?.ownerShip?.type &&
            dossier?.claim?.car?.ownerShip?.type !== 'businesslike'
              ? ('GlobalApplicativeConstants.ownerShipType.' +
                  dossier?.claim?.car?.ownerShip?.type | translate)
              : dossier?.claim?.car?.ownerShip?.name
          }}</span>
        </p>
      </aside>

      <sc-button
        *ngIf="displayValidateRequestBtn()"
        class="request-validate-button"
        buttonType="primary"
        [disabled]="dossier?.requestAppointment?.isValidatedByFitter"
        (click)="onValidateRequest()"
        >{{
          (dossier?.requestAppointment?.isValidatedByFitter
            ? 'DeclarationEndInformation.dossierIsValidatedBtn'
            : 'DeclarationEndInformation.markDossierValidatedBtn'
          ) | translate
        }}</sc-button
      >
    </section>
    <span
      *ngIf="
        (featureFlippingService.isFeatureActivated(
          'b2b-can-declare-sinister-comment'
        ) &&
          !!dossier?.claim?.sinisterComment) ||
        !!dossier?.fitterInfo?.message
      "
    >
      <sc-sinister-comment [dossier]="dossier"></sc-sinister-comment>
    </span>
  `,
})
export class DeclarationEndInformationComponent {
  @Input() dossier: Dossier;
  @Input() canValidateRequest: boolean;

  @Output() notifyCustomerEvent = new EventEmitter<void>();
  @Output() validateRequestEvent = new EventEmitter<void>();

  insuranceMessage$ = this.insuranceCoverService.getInsuredMessage();
  coverMessage$ = this.insuranceCoverService.getCoverMessage();
  coverRangeMessage$ = this.insuranceCoverService.getCoverRangeMessage();

  constructor(
    public featureFlippingService: FeatureFlippingManagerService,
    public translator: TranslatorService,
    private readonly insuranceCoverService: InsuranceCoverSandboxService
  ) {}

  isDossierAbandonned() {
    return this.dossier?.state === 'ABANDONED';
  }

  getFormattedInsuranceName() {
    switch (this.dossier?.policyInformation?.insuranceChoice) {
      case 'unknown':
        return this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.action.unknown'
        );
      case 'false':
        return this.translator.getInstantTranslation(
          `GlobalApplicativeConstants.action.no`
        );
      case 'true':
        return !this.dossier?.policyInformation?.insuranceName?.length
          ? this.translator.getInstantTranslation(
              `GlobalApplicativeConstants.action.yes`
            )
          : this.dossier?.policyInformation?.insuranceName;
      default:
        return !this.dossier?.policyInformation?.insuranceName?.length
          ? this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.unknown'
            )
          : this.dossier?.policyInformation?.insuranceName;
    }
  }

  notifyCustomer() {
    this.notifyCustomerEvent.emit();
  }

  displayValidateRequestBtn() {
    return (
      this.canValidateRequest &&
      this.isRequestAppointment() &&
      this.featureFlippingService.isBrandFeatureActivated(
        this.dossier.requestAppointment.brandId,
        'can-validate-request-appointment'
      )
    );
  }

  isRequestAppointment() {
    return !this.dossier?.appointment && this.dossier?.requestAppointment;
  }

  onValidateRequest() {
    this.validateRequestEvent.emit();
  }
}
